import { ThemeProvider } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { TenantConfig } from '@askporter/grieg-types';
import Loading from '../../components/Loading';
import DynamicError from '../../pages/Error/Dynamic';
import { defaultConfig, getErrorStrings, remediateErrors, reportErrors, validateConfig } from './utils';

import { FatalError } from './types';
import { BrowserRouter } from 'react-router-dom';
import { fallbackTheme } from '../Theme/utils';
import _ from 'lodash';

interface ConfigProviderProps {
  children: React.ReactNode[] | React.ReactNode;
}

// Assign a set of initial values for the context. Once the config data has been fetched with React query we validate it
// and assign some more meaningful (usable) default/fallback values if anything is missing
export const ConfigContext = React.createContext<TenantConfig>({
  tenantName: '',
  tenantDisplay: {
    palette: defaultConfig.tenantDisplay.palette,
    links: {
      privacyPolicyUrl: '',
      termsOfUseUrl: '',
      landingZonePath: '',
    },
    logos: {
      logo: '',
      logoSmall: '',
      favicon: '',
    },
    coverImages: [],
  },
  tenantContact: {
    phoneNumber: '',
    email: '',
  },
  tenantBot: {
    botName: '',
    principalConversationTypeUrn: '',
    avatar: '',
  },
  tenantCognito: {
    userPool: '',
    appClientId: '',
    issuer: '',
  },
  tenantIntegrations: {
    analyticsId: 27,
    mapsKey: '',
  },
  featureFlags: {},
  tenantLanguages: [],
});

/**
 * Hook for obtaining the tenant data from the Config context
 *
 * @returns The config object
 */
export const useConfig = (): TenantConfig => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig hook must be used within an ConfigProvider component');
  }
  return context;
};

/**
 * Provider component which passes down the config/tenant data
 *
 * @param children - The React children components to wrap with the provider
 * @returns ConfigProvider component with children
 */
export const ConfigProvider: React.FC = ({ children }: ConfigProviderProps) => {
  const { data, isLoading, isSuccess } = useQuery('static/tenant');
  const [isDataValidated, setIsDataValidated] = useState<boolean>(false);
  const [fatalError, setFatalError] = useState<FatalError>();
  const [validatedData, setValidatedData] = useState<TenantConfig>({ ...(data as TenantConfig) });

  // Once the config data has been retrieved, validate it, resolve and report any errors.
  useEffect(() => {
    if (!isLoading) {
      const configData = { ...(data as TenantConfig) };
      const { configErrors, fatalError: fatalErr } = validateConfig(configData);
      if (fatalErr) setFatalError(fatalErr);

      if (Object.keys(configErrors).length > 0) {
        remediateErrors(configErrors, configData);
        reportErrors(configErrors);
      }

      if (fatalError === undefined) setIsDataValidated(true);

      setValidatedData((prevValues) => {
        return { ...prevValues, ...configData };
      });
    }
  }, [isLoading]);

  // If we hit a fatal error, we render a minimal version of the app withe a fallback theme, without this we would need
  // to consider including various providers (such as )
  if (fatalError && !isLoading) {
    const errorStrings = getErrorStrings();

    return (
      <ConfigContext.Provider value={validatedData}>
        <ThemeProvider theme={fallbackTheme}>
          <BrowserRouter>
            <DynamicError
              errTitle={errorStrings.pageTitle}
              errMsg={errorStrings.subtitle}
              errMetaTitle={errorStrings.pageTitle}
              errLinkText={errorStrings.linkText}
            />
          </BrowserRouter>
        </ThemeProvider>
      </ConfigContext.Provider>
    );
  }
  // display a loader while we validate and fetch config
  else if (!isDataValidated || (isLoading && !isSuccess)) return <Loading />;
  else return <ConfigContext.Provider value={validatedData}>{children}</ConfigContext.Provider>;
};
